<template>
  <div>
    <Heading title="Products" subTitle="Configure look &amp; feel of AMP for products." />

    <div class="accordion mb-45" id="product-collapse-group">
      <CollapseItem label="Title" parent="#product-collapse-group" highlight="h1.product-title" open>
        <Customizer
          category="productTitle"
          basic
          font
          space>
          <div class="row row-space-4">
            <div class="col-md-6">
              <label>Text Color</label>
              <ColorCustomizer
                category="productTitle" type="color" />
            </div>
            <div class="col-md-6">
              <label>Position related to thumbnails</label>
              <LayoutOptionsCustomizer
                category="product_title"
                :options="[ 'above', 'below' ]" />
            </div>
          </div>
        </Customizer>
      </CollapseItem>

      <CollapseItem label="Thumbnails" parent="#product-collapse-group" highlight=".thumbnails">
        <Customizer
          basic>
          <label>Layout</label>
          <div class="row">
            <div class="col-md-6">
              <LayoutOptionsCustomizer
                category="product_thumbnails"
                :options="[ 'horizontal', 'vertical' ]" />
            </div>
          </div>
        </Customizer>
      </CollapseItem>

      <CollapseItem label="Collection Name" parent="#product-collapse-group" highlight="p.product-collection" toggle="product_collection">
        <Customizer
          category="productCollection"
          basic
          font
          space :spaceConfig="{ hidePadding: true }">
          <div class="row row-space-4">
            <div class="col-md-6">
              <label>Text Color</label>
              <ColorCustomizer
                category="productCollection" type="color" />
            </div>
            <div class="col-md-6">
              <label>Prefix</label>
              <input type="text" class="form-control" v-model="language__collection_in_text">
            </div>
          </div>
        </Customizer>
      </CollapseItem>

      <CollapseItem label="Vendor Name" parent="#product-collapse-group" highlight="p.product-vendor" toggle="product_vendor">
        <Customizer
          category="productVendor"
          basic
          font
          space :spaceConfig="{ hidePadding: true }">
          <div class="row row-space-4">
            <div class="col-md-6">
              <label>Text Color</label>
              <ColorCustomizer
                category="productVendor" type="color" />
            </div>
            <div class="col-md-6">
              <label>Prefix</label>
              <input type="text" class="form-control" v-model="language__vendor_in_text">
            </div>
          </div>
        </Customizer>
      </CollapseItem>

      <CollapseItem label="Product Type" parent="#product-collapse-group" highlight="p.product-type" toggle="product_type">
        <Customizer
          category="productType"
          basic
          font
          space :spaceConfig="{ hidePadding: true }">
          <div class="row row-space-4">
            <div class="col-md-6">
              <label>Text Color</label>
              <ColorCustomizer
                category="productType" type="color" />
            </div>
          </div>
        </Customizer>
      </CollapseItem>

      <CollapseItem label="Inline Review" parent="#product-collapse-group" highlight=".review-title" toggle="product_review">
        <Customizer
          category="productReview"
          basic
          font :fontConfig="{ hideTextTransform: true }"
          space :spaceConfig="{ hidePadding: true }">
          <Alert icon="IconImportant" class="alert-attention" v-if="reviewsMap[review] && !miniReviewSupported.includes(review)">
            <p class="mb-0">{{reviewsMap[review]}} doesn't support Inline Review.</p>
          </Alert>
          <p class="font-weight-normal">In the preview, only Multiple Review Text will be shown.</p>
          <div class="row row-space-4">
            <div class="col-md-6">
              <label>Single Review Text</label>
              <input type="text" class="form-control" v-model="language__single_review_text">
            </div>
            <div class="col-md-6">
              <label>Multiple Review Text</label>
              <input type="text" class="form-control" v-model="language__multiple_review_text">
            </div>
          </div>
          <div class="row row-space-4">
            <div class="col-md-6">
              <label>Star Color</label>
              <ColorCustomizer
                category="productReviewStar"
                type="color" />
            </div>
            <div class="col-md-6">
              <label>Text Color</label>
              <ColorCustomizer
                category="productReviewText"
                type="color" />
            </div>
          </div>
          <div class="row row-space-4">
            <div class="col-md-6">
              <div class="form-group form-check m-0">
                <input type="checkbox" class="form-check-input" id="hideIfNoReviews" v-model="layout__hide_no_review">
                <label class="form-check-label" for="hideIfNoReviews"><small>Hide if no reviews found</small></label>
              </div>
            </div>
          </div>
        </Customizer>
      </CollapseItem>
      <CollapseItem label="Inventory Status" parent="#product-collapse-group" highlight=".availability.h4" toggle="product_inventory_status">
        <Customizer
          category="productInventoryStatus"
          basic
          font :fontConfig="{ hideTextTransform: true }"
          space :spaceConfig="{ hidePadding: true }">
          <label>In Stock</label>
          <div class="row mb-3">
            <div class="col-md-6">
              <input type="text" class="form-control" v-model="language__in_stock_text">
            </div>
            <div class="col-md-6">
              <ColorCustomizer
                category="productInStock"
                type="color" />
            </div>
          </div>
          <label>Out of Stock</label>
          <div class="row mb-3">
            <div class="col-md-6">
              <input type="text" class="form-control" v-model="language__out_of_stock_text">
            </div>
            <div class="col-md-6">
              <ColorCustomizer
                category="productOutOfStock"
                type="color" />
            </div>
          </div>
          <label>Will be back by</label>
          <div class="row mb-3">
            <div class="col-md-6">
              <input type="text" class="form-control" v-model="language__will_be_back_by_text">
            </div>
            <div class="col-md-6">
              <ColorCustomizer
                category="productWillBeBackBy"
                type="color" />
            </div>
          </div>
          <label>Will not ship until</label>
          <div class="row">
            <div class="col-md-6">
              <input type="text" class="form-control" v-model="language__will_not_ship_until_text">
            </div>
          </div>
        </Customizer>
      </CollapseItem>
      <CollapseItem label="Product Page Link" parent="#product-collapse-group" highlight="p.full-link" toggle="product_page_link">
        <Customizer
          category="productPageLink"
          basic
          font
          space :spaceConfig="{ hidePadding: true }">
          <div class="row row-space-4">
            <div class="col-md-6">
              <label>Text Color</label>
              <ColorCustomizer
                category="productPageLink" type="color" />
            </div>
            <div class="col-md-6">
              <label>Link text</label>
              <textarea class="form-control" v-model="language__product_page_link_text"></textarea>
            </div>
          </div>
        </Customizer>
      </CollapseItem>
      <CollapseItem label="Related Products" parent="#product-collapse-group" badge="Basic" highlight=".h5.related-products-title" toggle="related_products">
        <Customizer
          category="productRelatedProductsHeading"
          basic
          font
          space
          border :borderConfig="{ hideBorderRadius: true }">
          <label>Layout</label>
          <div class="row row-space-4">
            <div class="col-md-6">
              <LayoutOptionsCustomizer
                category="recommendations"
                :options="[ 'grid', 'carousel' ]" />
            </div>
          </div>
          <div class="row row-space-4 mb-4">
            <div class="col-md-6">
              <label>Text Color</label>
              <ColorCustomizer
                category="productRelatedProductsHeading" type="color" />
            </div>
            <div class="col-md-6">
              <label>Heading Text</label>
              <input type="text" class="form-control" v-model="language__related_products_title_text">
            </div>
          </div>

          <p class="font-weight-normal mb-0">Go to <router-link :to="{ name: 'amp_collections' }">Collection</router-link> to customize the product grid.</p>
        </Customizer>
      </CollapseItem>
    </div>

    <Heading h2 title="Cart &amp; Pricing" subTitle="Configure style of price &amp; cart buttons." />

    <div class="accordion">
      <CollapseItem label="Price Bar" parent="#product-collapse-group" highlight="p.price" toggle="hide_pricing">
        <Customizer
          category="productPriceBar"
          basic>
          <FontCustomizer category="productPriceBar" :config="{ hideFontFamily: true, hideFontSize: true, hideTextTransform: true }" />
        </Customizer>
      </CollapseItem>
      <CollapseItem label="Sale Price" parent="#product-collapse-group" highlight=".h4.original-price">
        <Customizer
          category="productSalePrice"
          basic
          font :fontConfig="{ hideTextTransform: true, hideTextAlign: true }">
          <div class="row row-space-4">
            <div class="col-md-6">
              <label>Text Color</label>
              <ColorCustomizer
                category="productSalePrice" type="color" />
            </div>
          </div>
        </Customizer>
      </CollapseItem>
      <CollapseItem label="Compare at price" parent="#product-collapse-group" highlight=".h5.compare-at-price">
        <Customizer
          category="productCompareAtPrice"
          basic
          font :fontConfig="{ hideTextTransform: true, hideTextAlign: true }">
          <div class="row row-space-4">
            <div class="col-md-6">
              <label>Text Color</label>
              <ColorCustomizer
                category="productCompareAtPrice" type="color" />
            </div>
          </div>
        </Customizer>
      </CollapseItem>
      <CollapseItem label="Savings" parent="#product-collapse-group" highlight=".price > .product-save" toggle="product_savings">
        <Customizer
          category="productSavings"
          basic
          font :fontConfig="{ hideTextTransform: true, hideTextAlign: true }"
          space :spaceConfig="{ hideMargin: true }"
          border>
          <div class="row row-space-4">
            <div class="col-md-6">
              <label>Text Color</label>
              <ColorCustomizer
                category="productSavings" type="color" />
            </div>
            <div class="col-md-6">
              <label>Text</label>
              <input type="text" class="form-control" v-model="language__save_text">
            </div>
          </div>
          <div class="row row-space-4">
            <div class="col-md-6">
              <div class="form-group form-check m-0">
                <input type="checkbox" class="form-check-input" id="savingsInPerc" v-model="layout__savings_perc">
                <label class="form-check-label" for="savingsInPerc"><small>Show Savings in percentage</small></label>
              </div>
            </div>
          </div>
        </Customizer>
      </CollapseItem>

      <CollapseItem label="Variant Selector" parent="#product-collapse-group" highlight=".variant-selector" toggle="hide_variant_selector">
        <Customizer
          category="variantSelector"
          basic
          font
          space :spaceConfig="{ hidePadding: true }">
          <div class="row row-space-4">
            <div class="col-md-6">
              <label>Text Color</label>
              <ColorCustomizer
                category="variantSelector" type="color" />
            </div>
          </div>
        </Customizer>
      </CollapseItem>

      <CollapseItem label="Quantity Selector" parent="#product-collapse-group" highlight=".product-quantity-selector" toggle="hide_quantity">
        <Customizer
          category="quantitySelector"
          basic
          space :spaceConfig="{ hidePadding: true }">
          <label>Radius</label>
          <div class="row customizer-trbl">
            <div class="col-md-3">
              <div class="input-group">
                <input type="number" class="form-control text-center" @input="handleQuantitySelectorRadius" :value="quantitySelectorRadius">
                <div class="input-group-append">
                  <span class="input-group-text" title="Pixel">PX</span>
                </div>
              </div>
            </div>
          </div>
        </Customizer>
      </CollapseItem>

      <CollapseItem label="Add to Cart Button" parent="#product-collapse-group" highlight=".btn-add-to-cart">
        <Customizer
          category="addToCartButton"
          basic
          font :fontConfig="{ hideTextAlign: true, hideTextTransform: true }"
          space
          border>
          <div class="row row-space-4 mb-4">
            <div class="col-md-6">
              <label>Background Color</label>
              <ColorCustomizer
                category="addToCartButton" type="backgroundColor" setTextColor />
            </div>
            <div class="col-md-6">
              <label>Text Color</label>
              <ColorCustomizer
                category="addToCartButton" type="color" />
            </div>
          </div>

          <FontCustomizer class="mb-4" category="addToCartButtons" :config="{ hideFontFamily: true, hideFontSize: true, hideTextTransform: true }" />

          <div class="row row-space-4">
            <div class="col-md-6">
              <label>Text</label>
              <input type="text" class="form-control" v-model="language__add_to_cart_text">
            </div>
            <div class="col-md-6">
              <label>URL</label>
              <input type="text" class="form-control" v-model="language__add_to_cart_url">
              <small class="form-text text-muted">If this field isn't blank, customers will be taken to this URL when they click on Add to cart button.</small>
              <small class="form-text text-muted"><strong>Variables</strong>: <kbd>[PRODUCT_URL]</kbd>, <kbd>[PRODUCT_TITLE]</kbd></small>
              <small class="form-text text-muted"><strong>Example</strong>: <kbd>/account/login?checkout_url=[PRODUCT_URL]</kbd></small>
            </div>
          </div>
        </Customizer>
      </CollapseItem>

      <CollapseItem label="Quick Buy" parent="#product-collapse-group" highlight=".btn-quick-buy" toggle="product_quick_buy" badge="Basic">
        <Customizer
          category="quickBuyButton"
          basic
          font :fontConfig="{ hideTextAlign: true, hideTextTransform: true }"
          space
          border>
          <div class="row row-space-4">
            <div class="col-md-6">
              <label>Text</label>
              <input type="text" class="form-control" v-model="language__quick_buy_text">
            </div>
          </div>
          <div class="row row-space-4">
            <div class="col-md-6">
              <label>Background Color</label>
              <ColorCustomizer
                category="quickBuyButton" type="backgroundColor" setTextColor />
            </div>
            <div class="col-md-6">
              <label>Text Color</label>
              <ColorCustomizer
                category="quickBuyButton" type="color" />
            </div>
          </div>
        </Customizer>
      </CollapseItem>
    </div>

    <SaveButton :hasChanged="hasChanged" @click="saveMaster" bar discard />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

import CollapseItem from '@/components/CollapseItem'
import Customizer from '@/components/customizers/Customizer'

import ColorCustomizer from '@/components/customizers/ColorCustomizer'
import SaveButton from '@/components/SaveButton'
import LayoutOptionsCustomizer from '@/components/customizers/LayoutOptionsCustomizer'
import FontCustomizer from '@/components/customizers/FontCustomizer'

import update from '@/mixins/update'
import filters from '@/mixins/filters'
import watchForChange from '@/mixins/watchForChange'
import mapMultiComputed from '@/mixins/mapMultiComputed'
import { reviewsMap, miniReviewSupported } from '@/utils/reviews'

export default {
  mixins: [
    update,
    filters,
    watchForChange(),
    mapMultiComputed('settings', [
      'language.collection_in_text',
      'language.vendor_in_text',
      'language.product_page_link_text',
      'language.related_products_title_text',
      'language.single_review_text',
      'language.multiple_review_text',
      'language.in_stock_text',
      'language.out_of_stock_text',
      'language.will_be_back_by_text',
      'language.will_not_ship_until_text',
      'language.save_text',
      'language.add_to_cart_text',
      'language.add_to_cart_url',
      'language.quick_buy_text',
      'layout.savings_perc',
      'layout.hide_no_review',
      'review'
    ]),
    mapMultiComputed('amptheme', [
      'quantitySelectorLeftButton.borderBottomLeftRadius',
      'quantitySelectorLeftButton.borderTopLeftRadius',
      'quantitySelectorRightButton.borderBottomRightRadius',
      'quantitySelectorRightButton.borderTopRightRadius'
    ])
  ],
  components: {
    Customizer,
    CollapseItem,
    ColorCustomizer,
    SaveButton,
    LayoutOptionsCustomizer,
    FontCustomizer
  },
  computed: {
    ...mapState([ 'currentPreviewType' ]),
    quantitySelectorRadius: function () {
      return this.removeUnit(this.quantitySelectorLeftButton__borderBottomLeftRadius)
    },
    reviewsMap: function () {
      return reviewsMap
    },
    miniReviewSupported: function () {
      return miniReviewSupported
    }
  },
  methods: {
    ...mapActions([ 'saveMaster' ]),
    handleQuantitySelectorRadius: function (e) {
      const value = this.addUnit(e.target.value, 'px')

      this.quantitySelectorLeftButton__borderBottomLeftRadius = value
      this.quantitySelectorLeftButton__borderTopLeftRadius = value
      this.quantitySelectorRightButton__borderBottomRightRadius = value
      this.quantitySelectorRightButton__borderTopRightRadius = value
    }
  },
  mounted: function () {
    if (this.currentPreviewType !== 'product') {
      this.$store.commit('SWITCH_PREVIEW_TYPE', 'product')
    }
  }
}
</script>
